/**
 * 2026-10-07 · a SAMPLE step's sampling — the /process-config Sampling dialog (mbmr/Sampling.js)
 * on the step: the sample, its quantity, the department it goes to, the test, QC limits and the
 * conditional parameter. Written to the same rows that dialog writes (services/recipe/sampling.py).
 * The lists are the ones that dialog reads, fetched once per page.
 */
import React from "react";
import { Input, InputNumber, Select, Switch } from "antd";
import { Field } from "./Field";
import { useSamplingLists } from "./lists";

export function SamplingSettings({ step, readOnly, onStage }) {
  const lists = useSamplingLists();
  const sm = step.sampling || {};
  const put = (patch) => onStage({ sampling: { ...sm, ...patch } });
  // 2026-10-13 · only by-products are offered. A step that already samples something else keeps
  // it, shown by name and marked, until the author picks a by-product in its place.
  const offered = lists.samples.flatMap((g) => g.options);
  const legacy = sm.sample_id != null && !offered.some((o) => String(o.value) === String(sm.sample_id))
    ? (lists.allMaterials || {})[String(sm.sample_id)] : null;
  const sampleOptions = legacy
    ? [...lists.samples, { label: "Current — not a by-product", options: [
        { value: sm.sample_id, label: `${legacy.label} (${legacy.type || "not a by-product"})`, unit: legacy.unit }] }]
    : lists.samples;
  const unit = offered.find((o) => String(o.value) === String(sm.sample_id))?.unit || legacy?.unit || sm.unit || "";
  const pUnit = lists.parameters.find((o) => String(o.value) === String(sm.parameter_id))?.unit || sm.parameter_unit || "";
  const req = (label) => <>{label}<span className="u0-req" aria-label="required"> *</span></>;
  return (
    <div className="u0-grid2" data-testid="sampling">
      <Field label={req("Sample")}>
        <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
                value={sm.sample_id ?? undefined} options={sampleOptions} placeholder="— by-product —"
                status={legacy ? "warning" : (!sm.sample_id && step.sampling ? "error" : undefined)} notFoundContent="No by-product material in this plant — add one in Material Master (type By Product)."
                onChange={(v) => put({ sample_id: v ?? null })} />
      </Field>
      <Field label={req(`Quantity${unit ? ` (${unit})` : ""}`)}>
        <InputNumber size="small" min={0} style={{ width: "100%" }} disabled={readOnly} value={sm.quantity ?? null}
                     onChange={(v) => put({ quantity: v })} />
      </Field>
      <Field label={req("To department")}>
        <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
                value={sm.to_id ?? undefined} options={lists.departments} placeholder="— choose —"
                onChange={(v, o) => put({ to_id: v ?? null,
                  ...(/quality|qc/i.test(o?.label || "") ? { is_qc: true } : {}) })} />
      </Field>
      <Field label={req("Test type")}>
        <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
                value={sm.test_type_id ?? undefined} options={lists.tests} placeholder="— choose —"
                onChange={(v) => put({ test_type_id: v ?? null })} />
      </Field>
      <Field label="QC sampling" hint="QC: the sample goes to quality control with its own limits. Off: an inline sample.">
        <div><Switch size="small" disabled={readOnly} checked={!!sm.is_qc} checkedChildren="Yes" unCheckedChildren="No"
                     onChange={(v) => put({ is_qc: v, ...(v ? {} : { sampling_min: null, sampling_max: null }) })} /></div>
      </Field>
      {sm.is_qc ? (
        <Field label={req("Sampling limits (min – max)")}>
          <div className="u0-mon-pair">
            <InputNumber size="small" value={sm.sampling_min ?? null} disabled={readOnly} placeholder="min"
                         onChange={(v) => put({ sampling_min: v })} />
            <span className="u0-mon-dash">–</span>
            <InputNumber size="small" value={sm.sampling_max ?? null} disabled={readOnly} placeholder="max"
                         onChange={(v) => put({ sampling_max: v })} />
          </div>
        </Field>
      ) : <span />}
      <Field label="Conditional parameter" hint="Sample only when this parameter is within the range below.">
        <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
                value={sm.parameter_id ?? undefined} options={lists.parameters} placeholder="— none —"
                onChange={(v) => put({ parameter_id: v ?? null, ...(v ? {} : { min: null, max: null, description: "" }) })} />
      </Field>
      {sm.parameter_id ? (
        <>
          <Field label={req(`Range${pUnit ? ` (${pUnit})` : ""}`)}>
            <div className="u0-mon-pair">
              <InputNumber size="small" value={sm.min ?? null} disabled={readOnly} placeholder="min"
                           onChange={(v) => put({ min: v })} />
              <span className="u0-mon-dash">–</span>
              <InputNumber size="small" value={sm.max ?? null} disabled={readOnly} placeholder="max"
                           onChange={(v) => put({ max: v })} />
            </div>
          </Field>
          <Field label={req("Description")}>
            <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 4 }} disabled={readOnly}
                            value={sm.description || ""} onChange={(e) => put({ description: e.target.value })} />
          </Field>
        </>
      ) : null}
    </div>
  );
}
